/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { LogService, UtilsService } from '../../../../../../core/services';
import { WizardTitleEnum } from '../../../enums';

@Injectable()
export class WizardControllerStateService {
    constructor(
        private logService: LogService,
        private utilsService: UtilsService
    ) {
    }

    /**
     * Push temporary id to the success controller state.
     * @param controllerState
     * @param serviceType
     * @param tempId
     * @param id
     */
    public pushIdToSuccessArray(controllerState: any, serviceType: string, id: string, tempId: string): void {
        this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum.SUCCESS);
        this.utilsService.createArrayIfNotExist(controllerState[WizardTitleEnum.SUCCESS], serviceType);

        this.logService.debug('wizardControllerState:setSuccessState()', 'Setting success state for ' + serviceType);

        if (tempId.startsWith('temp-') || tempId.startsWith('$')) {
            const lookup = { tempId: tempId, id: id };
            controllerState[WizardTitleEnum.SUCCESS][serviceType].push(lookup);
        } else {
            this.logService.debug('wizardControllerState:updateSuccessState()', 'Missing tempId', controllerState);
        }
    }

    /**
     * Check if the temp ids are unique, if they are not its probably a partner template.
     * @param successArray
     */
    public tempIdsAreUnique(successArray: any[]): boolean {
        if (successArray.length === 1) {
            return true;
        }

        return !successArray.every((item: any): boolean => item.tempId === successArray[0].tempId);
    }

    /**
     * Get service values from service data.
     * @param controllerState
     * @param serviceData
     */
    public getValuesFromServiceData(controllerState: any, serviceData: any): any {
        const propertiesToIgnore = this.utilsService.getObjectValue(
            controllerState, `${WizardTitleEnum.REFERENCE}.${WizardTitleEnum.PROPERTIES}`
        );
        const serviceValues = {};
        for (const property in serviceData) {
            if (propertiesToIgnore?.includes(property)) {
                serviceValues[property] = serviceData[property].value;
                const deleteIndex = propertiesToIgnore.findIndex((item: string): boolean => item === property);
                if (deleteIndex !== -1) {
                    propertiesToIgnore.splice(deleteIndex, 1);
                }
            } else if (serviceData.hasOwnProperty(property) &&
                (serviceData[property].value !== undefined && serviceData[property].value !== null)) {
                serviceValues[property] = serviceData[property].value;
            } else {
                // Remove properties with null values.
                delete serviceValues[property];
            }
        }

        return serviceValues;
    }

    /**
     * Add service to controller state.
     * @param controllerState
     * @param WizardTitleEnum
     * @param template
     */
    public addServiceToControllerState(controllerState: any, WizardTitleEnum: string, template: any): void {
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        if (services) {
            services.push(template);
        } else {
            this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum);
            controllerState[WizardTitleEnum].push(template);
        }
    }

    /**
     * Update service in controller state by id.
     * @param controllerState
     * @param WizardTitleEnum
     * @param serviceId
     * @param template
     */
    public updateServiceInControllerStateById(controllerState: any, WizardTitleEnum: string, serviceId: string, template: any): void {
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        if (!services) {
            this.logService.warn(
                'WizardControllerStateService:updateServiceInControllerStateById',
                'No service found with type ' + WizardTitleEnum
            );
        } else {
            const updateIndex = services.findIndex(item => item.id.value === serviceId);
            if (updateIndex !== -1) {
                services[updateIndex] = template;
            } else {
                this.logService.warn(
                    'WizardControllerStateService:updateServiceInControllerStateById',
                    'Unable to update item with id ' + serviceId
                );
            }
        }
    }

    /**
     * Delete item in the controller state by id.
     * @param controllerState
     * @param WizardTitleEnum
     * @param serviceId
     */
    public deleteServiceFromControllerStateByServiceId(controllerState: any, WizardTitleEnum: string, serviceId: string): void {
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        if (!services) {
            this.logService.warn(
                'WizardControllerStateService:deleteServiceFromControllerStateByServiceId',
                'No service found with type ' + WizardTitleEnum
            );
        } else {
            const deleteIndex: number = services.findIndex((item: any): boolean => item.id.value === serviceId);
            if (deleteIndex !== -1) {
                services.splice(deleteIndex, 1);
            } else {
                this.logService.warn(
                    'WizardControllerStateService:deleteServiceFromControllerStateByServiceId',
                    'Unable to update item with id ' + serviceId
                );
            }
        }
    }

    /**
     * Update service item in controller state by id.
     * @param controllerState
     * @param WizardTitleEnum
     * @param serviceId
     * @param property
     * @param newValue - must be sent in the correct type is if the property
     */
    public updateServiceItemInControllerStateById(
        controllerState: any,
        WizardTitleEnum: string,
        serviceId: string,
        property: string,
        newValue: any
    ): void {
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        const index: number = services.findIndex((item: any): boolean => item.id.value === serviceId);
        if (index === -1) {
            this.logService.warn(
                'WizardControllerStateService:updateServiceItemInControllerStateById', 'Can\'t find a service to update'
            );
        } else {
            const service: any = services[index];
            this.updateValueInService(service, property, newValue);
        }
    }

    /**
     * Delete an item in a service controller state by service id.
     * @param controllerState
     * @param WizardTitleEnum
     * @param serviceId
     * @param property
     * @param propertyLookup
     */
    public deleteServiceItemInControllerStateById(
        controllerState: any,
        WizardTitleEnum: string,
        serviceId: string,
        property: string,
        propertyLookup: string
    ): void {
        const services: any = this.utilsService.getObjectValue(controllerState, `${WizardTitleEnum}`);
        const index: number = services.findIndex((item: any): boolean => item.id.value === serviceId);
        if (index === -1) {
            this.logService.warn(
                'WizardControllerStateService:deleteServiceItemInControllerStateById', 'Can\'t find a service to update'
            );
        } else {
            const service: any = services[index];
            const currentValue: any = service[property].value;
            this.deleteServiceValue(service, currentValue, property, propertyLookup);
        }
    }

    /**
     * Delete value in array.
     * @param service
     * @param currentValue
     * @param property
     * @param propertyLookup
     * @private
     */
    private deleteServiceValue(service: any, currentValue: any[], property: string, propertyLookup: string): void {
        if (Array.isArray(currentValue)) {
            const deleteIndex: number = currentValue.findIndex((item: any): boolean => item === propertyLookup);
            if (deleteIndex !== -1) {
                currentValue.splice(deleteIndex, 1);
                service[property].value = currentValue;
            } else {
                this.logService.warn('WizardControllerStateService:deleteValueInService', 'Item not found in array');
            }
        } else {
            if (service[property]?.value) {
                service[property].value = null;
            } else {
                this.logService.warn('WizardControllerStateService:deleteValueInService', 'Unable to find item to delete');
            }
        }
    }

    /**
     * Update value in array.
     * @param service
     * @param property
     * @param newValue
     * @private
     */
    private updateValueInService(service: any, property: string, newValue: string): void {
        if (!service[property]?.value) {
            service[property] = {
                value: Array.isArray(newValue) ? [] : '',
                hidden: true
            };
        }

        const existingValue: any = service[property].value;
        if (Array.isArray(existingValue)) {
            service[property].value = existingValue.concat(newValue);
        } else {
            service[property].value = newValue;
        }
    }
}
